// Enhances capture forms in-place via DOM manipulation:
// - Adds a fixed white "+55" prefix in front of every type="tel" input
// - Inserts a per-field red error message under each input with the reason
// - Strips +55 from tel inputs' value (so submitLead never sees it)
//
// Designed to be safe to call from each capture page without touching
// page-specific submit/validation logic. It only augments visuals + UX.

import { applyPhoneMask, isValidBRPhone } from "./phoneMask";

const STYLE_ID = "capture-form-enhancer-styles";

const CSS = `
.cf-tel-wrap { position: relative; display: block; }
.cf-tel-prefix {
  position: absolute;
  left: 1px;
  top: 1px;
  bottom: 1px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  background: #1f2937;
  color: #ffffff;
  font-weight: 700;
  font-family: inherit;
  font-size: 0.95em;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  pointer-events: none;
  user-select: none;
  z-index: 2;
}
.cf-tel-wrap > input[type="tel"] { padding-left: 56px !important; }
.cf-field-error {
  display: none;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 600;
  margin-top: 5px;
  line-height: 1.35;
}
.cf-field-error.show { display: block; }
.cf-invalid { border-color: #dc2626 !important; }
`;

function ensureStyles(root: Document | ShadowRoot = document) {
  if ((root as Document).getElementById?.(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

function wrapTelInput(input: HTMLInputElement) {
  if (input.dataset.cfWrapped === "1") return;
  input.dataset.cfWrapped = "1";

  // Strip any +55 prefix from initial value
  input.value = input.value.replace(/^\s*\+?55\s*/, "");

  const parent = input.parentElement;
  if (!parent) return;

  const wrap = document.createElement("span");
  wrap.className = "cf-tel-wrap";
  parent.insertBefore(wrap, input);
  wrap.appendChild(input);

  const prefix = document.createElement("span");
  prefix.className = "cf-tel-prefix";
  prefix.textContent = "+55";
  wrap.insertBefore(prefix, input);

  // Mask handler
  const onInput = () => {
    // Defensive: never let a +55 typed/pasted survive
    if (/^\+?55/.test(input.value)) {
      input.value = input.value.replace(/^\+?55\s*/, "");
    }
    const masked = applyPhoneMask(input.value);
    if (input.value !== masked) input.value = masked;
  };
  input.addEventListener("input", onInput);
  input.addEventListener("blur", onInput);
}

function getOrCreateError(input: HTMLInputElement): HTMLElement {
  const existing = input.parentElement?.querySelector<HTMLElement>(
    ":scope > .cf-field-error"
  );
  if (existing) return existing;
  const div = document.createElement("div");
  div.className = "cf-field-error";
  // Insert after the wrapper (or input)
  const ref = input.closest(".cf-tel-wrap") ?? input;
  ref.parentElement?.insertBefore(div, ref.nextSibling);
  return div;
}

function showError(input: HTMLInputElement, msg: string | null) {
  const err = getOrCreateError(input);
  if (msg) {
    err.textContent = msg;
    err.classList.add("show");
    input.classList.add("cf-invalid");
  } else {
    err.textContent = "";
    err.classList.remove("show");
    input.classList.remove("cf-invalid");
  }
}

function validateField(input: HTMLInputElement) {
  const value = input.value.trim();
  if (!value) {
    // Don't show empty-required error during typing; only on blur if empty
    showError(input, null);
    return;
  }
  if (input.type === "tel") {
    const r = isValidBRPhone(value);
    showError(input, r.ok ? null : r.reason ?? "Número inválido.");
    return;
  }
  if (input.type === "email") {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    showError(
      input,
      ok ? null : "E-mail inválido — verifique o endereço informado."
    );
    return;
  }
  if (input.name === "nome" || input.name === "form_fields[name]" || /nome|name/i.test(input.id)) {
    const ok = value.length >= 2;
    showError(input, ok ? null : "Informe seu nome completo.");
    return;
  }
  showError(input, null);
}

/**
 * Enhance every capture form found inside `root` (defaults to document).
 * Idempotent — safe to call multiple times.
 */
export function enhanceCaptureForms(root: ParentNode = document): () => void {
  ensureStyles();

  const tels = Array.from(
    root.querySelectorAll<HTMLInputElement>('input[type="tel"]')
  );
  tels.forEach(wrapTelInput);

  // Per-field error on blur for tel + email + nome inputs
  const watched = Array.from(
    root.querySelectorAll<HTMLInputElement>(
      'input[type="tel"], input[type="email"], input[type="text"]'
    )
  );
  const cleanups: Array<() => void> = [];
  watched.forEach((input) => {
    const onBlur = () => validateField(input);
    const onInput = () => {
      if (input.classList.contains("cf-invalid")) validateField(input);
    };
    input.addEventListener("blur", onBlur);
    input.addEventListener("input", onInput);
    cleanups.push(() => {
      input.removeEventListener("blur", onBlur);
      input.removeEventListener("input", onInput);
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
